import { Fragment } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  RBI_COLORS,
  calcBrandIndexScore,
  calcDirectIndexScore,
  ACCESSIBILITY_RBI_COLORS,
} from "../../utils/brandIndexCalc";
import { ENTITY_LABELS } from "../../utils/labelMapping";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));


export default function RBIFormulaBreakdown({
  rank,
  favorability,
  brandPillarsAvg,
  purchaseCriteriaAvg,
  pillarsLabel = ENTITY_LABELS.brandPillar,
  isAccessibility = false,
  isComposite = false,
  isDirect = false,
  multiFactor = null,
}) {
  if ((isAccessibility || isComposite) && multiFactor) {
    return (
      <MultiFactorTable multiFactor={multiFactor} pillarsLabel={pillarsLabel} isComposite={isComposite} />
    );
  }

  if (isDirect) {
    return (
      <DirectFactorTable rank={rank} favorability={favorability} brandPillarsAvg={brandPillarsAvg} pillarsLabel={pillarsLabel} />
    );
  }

  const { total, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions } = calcBrandIndexScore({
    rank, favorability, brandPillarsAvg, purchaseCriteriaAvg,
  });

  const COLORS = RBI_COLORS;
  const normMax = 100;

  const fv = {
    visibility: { raw: rank ?? "—", formula: `${rank ?? "—"} × 2`, norm: visNorm },
    sentiment: { raw: favorability ?? "—", formula: `${favorability ?? "—"} × 2`, norm: sentNorm },
    brandPillars: {
      raw: brandPillarsAvg != null ? brandPillarsAvg.toFixed(2) : "—",
      formula: brandPillarsAvg != null ? `${brandPillarsAvg.toFixed(2)} × 10` : "— × 10",
      norm: pillarsNorm,
    },
    purchaseCriteria: {
      raw: purchaseCriteriaAvg != null ? purchaseCriteriaAvg.toFixed(2) : "—",
      formula: purchaseCriteriaAvg != null ? `${purchaseCriteriaAvg.toFixed(2)} × 10` : "— × 10",
      norm: purchaseNorm,
    },
  };

  const tableRows = [
    { key: "visibility",       label: ENTITY_LABELS.rank,             ...fv.visibility },
    { key: "sentiment",        label: "Sentiment",                    ...fv.sentiment },
    { key: "brandPillars",     label: pillarsLabel,                   ...fv.brandPillars },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria, ...fv.purchaseCriteria },
  ];

  return (
    <div>
      <TableContainer>
        <Table sx={{ width: "100%" }} size="medium">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Metric</StyledTableCell>
              <StyledTableCell align="right">Raw Score</StyledTableCell>
              <StyledTableCell align="right">Normalized (0–{normMax})</StyledTableCell>
              <StyledTableCell align="right">Contribution</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => (
              <StyledTableRow key={row.key}>
                <StyledTableCell component="th" scope="row">
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: COLORS[row.key],
                        flexShrink: 0,
                      }}
                    />
                    {row.label}
                  </span>
                </StyledTableCell>
                <StyledTableCell align="right">{row.raw}</StyledTableCell>
                <StyledTableCell align="right">
                  <strong>{Math.round(row.norm)}</strong>
                </StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 600 }}>
                  {contributions[row.key].toFixed(1)}
                </StyledTableCell>
              </StyledTableRow>
            ))}

            {/* Total row */}
            <TableRow sx={{ backgroundColor: "#0a1f44" }}>
              <TableCell
                colSpan={3}
                sx={{ color: "#fff", fontWeight: 700, fontSize: 15, borderBottom: "none" }}
              >
                Brand Index Score
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "#fff", fontWeight: 700, fontSize: 22, borderBottom: "none" }}
              >
                {total}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

const ACCESSIBILITY_METRIC_KEYS = ["visibility", "sentiment", "brandPillars"];
const RAW_KEY = { visibility: "visRaw", sentiment: "sentRaw", brandPillars: "pillarRaw" };
const NORM_KEY = { visibility: "visNorm", sentiment: "sentNorm", brandPillars: "pillarsNorm" };



function MultiFactorTable({ multiFactor, pillarsLabel, isComposite = false }) {
  const { total, breakdown, perFactorMax } = multiFactor;
  const COLORS = ACCESSIBILITY_RBI_COLORS;
  const metricLabel = (key) => (key === "visibility" ? ENTITY_LABELS.rank : key === "sentiment" ? "Sentiment" : pillarsLabel);

  return (
    <div>
      <TableContainer>
        <Table sx={{ width: "100%" }} size="medium">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Factor</StyledTableCell>
              <StyledTableCell>Metric</StyledTableCell>
              <StyledTableCell align="right">Raw Score</StyledTableCell>
              <StyledTableCell align="right">Normalized (0–{Math.round(perFactorMax)})</StyledTableCell>
              <StyledTableCell align="right">Contribution</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {breakdown.map((f) => (
              <Fragment key={f.factor}>
                {ACCESSIBILITY_METRIC_KEYS.map((metricKey, i) => (
                  <StyledTableRow key={`${f.factor}-${metricKey}`}>
                    {i === 0 && (
                      <TableCell
                        component="th"
                        scope="row"
                        rowSpan={ACCESSIBILITY_METRIC_KEYS.length + 1}
                        sx={{ fontWeight: 700, verticalAlign: "top", borderRight: "1px solid rgba(224,224,224,1)" }}
                      >
                        {f.factor}
                      </TableCell>
                    )}
                    <StyledTableCell>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: COLORS[metricKey],
                            flexShrink: 0,
                          }}
                        />
                        {metricLabel(metricKey)}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {f[RAW_KEY[metricKey]] != null
                        ? (metricKey === "brandPillars" ? Number(f[RAW_KEY[metricKey]]).toFixed(2) : f[RAW_KEY[metricKey]])
                        : "—"}
                    </StyledTableCell>
                    <StyledTableCell align="right"><strong>{Math.round(f[NORM_KEY[metricKey]])}</strong></StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 600 }}>
                      {f.contributions[metricKey].toFixed(1)}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                <StyledTableRow key={`${f.factor}-subtotal`}>
                  <StyledTableCell colSpan={3} sx={{ fontWeight: 700 }}>
                    {isComposite
                      ? `${f.factor} — Factor Score`
                      : `${f.factor} — Factor Score (${Math.round(f.perFactorMax)}% weight)`}
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ fontWeight: 700 }}>
                    {f.total.toFixed(1)} / {Math.round(f.perFactorMax)}
                  </StyledTableCell>
                </StyledTableRow>
              </Fragment>
            ))}

            {/* Grand total row — mean across factors for Composite, sum for Accessibility */}
            <TableRow sx={{ backgroundColor: "#0a1f44" }}>
              <TableCell
                colSpan={4}
                sx={{ color: "#fff", fontWeight: 700, fontSize: 15, borderBottom: "none" }}
              >
                {isComposite ? "Composite Index Score" : "Accessibility Index Score"}
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "#fff", fontWeight: 700, fontSize: 22, borderBottom: "none" }}
              >
                {total}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

function DirectFactorTable({ rank, favorability, brandPillarsAvg, pillarsLabel }) {
  const { total, visNorm, sentNorm, pillarsNorm, contributions } = calcDirectIndexScore({
    rank, favorability, brandPillarsAvg,
  });
  const COLORS = ACCESSIBILITY_RBI_COLORS;

  const rows = [
    { key: "visibility", label: ENTITY_LABELS.rank, raw: rank != null ? Math.round(rank) : "—", norm: visNorm },
    { key: "sentiment", label: "Sentiment", raw: favorability ?? "—", norm: sentNorm },
    { key: "brandPillars", label: pillarsLabel, raw: brandPillarsAvg != null ? brandPillarsAvg.toFixed(2) : "—", norm: pillarsNorm },
  ];

  return (
    <div>
      <TableContainer>
        <Table sx={{ width: "100%" }} size="medium">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Metric</StyledTableCell>
              <StyledTableCell align="right">Raw Score</StyledTableCell>
              <StyledTableCell align="right">Normalized (0–100)</StyledTableCell>
              <StyledTableCell align="right">Contribution</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.key}>
                <StyledTableCell component="th" scope="row">
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: COLORS[row.key],
                        flexShrink: 0,
                      }}
                    />
                    {row.label}
                  </span>
                </StyledTableCell>
                <StyledTableCell align="right">{row.raw}</StyledTableCell>
                <StyledTableCell align="right"><strong>{Math.round(row.norm)}</strong></StyledTableCell>
                <StyledTableCell align="right" sx={{ fontWeight: 600 }}>
                  {contributions[row.key].toFixed(1)}
                </StyledTableCell>
              </StyledTableRow>
            ))}

            <TableRow sx={{ backgroundColor: "#0a1f44" }}>
              <TableCell
                colSpan={3}
                sx={{ color: "#fff", fontWeight: 700, fontSize: 15, borderBottom: "none" }}
              >
                Direct Index Score
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: "#fff", fontWeight: 700, fontSize: 22, borderBottom: "none" }}
              >
                {total}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
